import "./TradeSimulationNewTradeComponent.scss";
import {IComponentOptions, IFormController, IOnDestroy, IOnInit, IScope} from "angular";
import {ComponentCallback} from "../../../../types/AngularJS";
import {NgFormUtil} from "../../../../js/util/NgFormUtil";
import {
    HistoricalOptionRecord,
    HistoricalPriceRange
} from "../../../../services/price/historical/HistoricalPriceModels";
import {ReplaySubject, Subject} from "rxjs";
import {CookbookService} from "../../../../services/cookbook/CookbookService";
import {Simulation} from "../../../models/Simulation";
import {TradeSimulatorViewStore} from "../../../service/TradeSimulatorViewStore";
import {OptionChainService} from "../../../../services/option-chain/OptionChainService";
import {SimpleModalService} from "../../../../types/SimpleModalService";
import {IsoDate} from "../../../../pseudo-types/DateTypes";

export const NewSimulationTrade = "tfNewSimulationTrade";

export function newSimulationTrade(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/simulation-data/new-trade/TradeSimulationNewTradeComponent.html",
        bindings: {
            viewStore: "<",
            underlying: "<",
            expiries: "<",
            onCreate: "&"
        },
        controllerAs: "ctrl",
        controller: TradeSimulationNewTradeComponentController
    };
}

export class TradeSimulationNewTradeComponentController implements IOnInit, IOnDestroy {

    public viewStore: TradeSimulatorViewStore;
    public underlying: string;
    public expiries: Array<Date>;
    public simulation: Simulation | null;
    public holidays: Array<Date>;
    public onCreate: ComponentCallback;

    public form: IFormController;

    public tradeDate: IsoDate | null;
    public quantity: number | null;
    public code: string | null;
    public price: number | null;
    public historicalPriceRange: HistoricalPriceRange;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope,
                       private cookbookService: CookbookService,
                       private optionChainService: OptionChainService,
                       private confirmationService: ConfirmationService,
                       private simpleModalService: SimpleModalService) {
    }

    public $onInit(): void {
        this.viewStore
            .currentDate
            .takeUntil(this.destroyed$)
            .filter(d => d != null)
            .subscribe(d => {
                this.$scope.$applyAsync(() => this.tradeDate = d);
            });
        this.viewStore
            .priceRange
            .takeUntil(this.destroyed$)
            .subscribe((priceRange) => {
                this.$scope.$applyAsync(() => this.historicalPriceRange = priceRange);
            });
        this.viewStore
            .holidays
            .takeUntil(this.destroyed$)
            .subscribe((holidays) => {
                this.$scope.$applyAsync(() => this.holidays = holidays);
            });

        this.viewStore
            .currentSimulation
            .takeUntil(this.destroyed$)
            .subscribe((simulation) => {
                this.$scope.$applyAsync(() => this.simulation = simulation);
            });
    }

    public addTrade() {
        if (this.tradeDate != null && this.quantity != null && this.code != null && this.price != null) {
            this.onCreate({code: this.code, quantity: this.quantity, date: this.tradeDate, price: this.price});
            this.reset();
        }
    }

    public optionSelected(option: HistoricalOptionRecord) {
        this.code = option.code;
        if (this.quantity == null) {
            this.quantity = 1;
        }

        if (this.price == null) {
            this.price = option.openPrice;
        }

        this.addTrade();
    }

    public async openCookbook() {
        if (this.simulation != null && this.tradeDate != null) {

            const tradeDate = this.tradeDate;

            const chain = await this.optionChainService.chainFor(this.underlying, tradeDate);
            // Skip upcoming expiry
            const expiries = chain.expiries.after(tradeDate);

            const scaleFactor = Math.abs(this.quantity || 1);
            const trades = await this.cookbookService.open(this.tradeDate, this.underlying, expiries);

            if (trades.length === 0) {
                this.simpleModalService.alert("Unable to find Options to satisfy strategy parameters");
            } else {

                let updatedSimulation = this.simulation;
                for (const {code, quantity} of trades) {

                    chain.contractForCode(code)
                         .ifPresent(c => {
                             const tradeQuantity = quantity * scaleFactor;
                             updatedSimulation = updatedSimulation.addTrade(code, tradeQuantity, tradeDate, c.openPrice);
                         });
                }
                this.viewStore.update(updatedSimulation);
            }
        }
    }

    public async clearSimulation() {
        if (await this.confirmationService.confirm("clearBackTestingTrades")) {
            this.viewStore.clearSimulation();
        }
    }

    private reset() {
        this.quantity = null;
        this.price = null;
        this.code = null;
        NgFormUtil.reset(this.form);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
